import React from "react";

// @mui
import styled from "@emotion/styled";
import { Tooltip } from "@mui/material";

// Customizable mui components
import Box from "../mui/Box";
import Stack from "../mui/Stack";
import Table from "../mui/Table";
import TableRow from "../mui/TableRow";
import TableHead from "../mui/TableHead";
import TableCell from "../mui/TableCell";
import TableBody from "../mui/TableBody";
import Typography from "../mui/Typography";
import Pagination from "../mui/Pagination";
import TableContainer from "../mui/TableContainer";

// common components
import MagicLoadingBox from "../common/MagicLoadingBox";


// types
interface HeatMapRowData {
  id: number;
  region: string;
  enrolment: string;
  femaleEnrolment: string;
  attendance: string;
  dropOut: string;
  placement: string;
  demandDriven: string;
  retention: string;
  operationsFinance: string;
  operationsHR: string;
  governanceAndEthics: string;
  sessionQuality: string;
  stackholdersFeedback: string;
  centreVisitAndBranding: string;
}

interface HeatMapTableStatusBtn {
  name: string;
  color: string;
}

interface Data {
  hitMapRowData: {[key: string]: string}[];
  hitMapHeaderData: string[];
}

interface Props {
  data: Data | {}
  isLoading: boolean;
  heatMapTableStatusBtn: HeatMapTableStatusBtn[];
  handleHitMapTablePagination: ( event: React.ChangeEvent<unknown>,page: number) => void;
}

const HeatMapTable: React.FC<Props> = ({
  data,
  isLoading,
  heatMapTableStatusBtn,
  handleHitMapTablePagination,
}) => {
     
  let hitMapHeaderData: string[] = []
  let hitMapRowData: {[key: string]: string}[] = [];

  // handle loading stats of data
  if (isLoading) return <MagicLoadingBox height={500}/>

  if("hitMapRowData" in data && "hitMapHeaderData" in data) {
    ({hitMapRowData, hitMapHeaderData} = data)
  }


  return (
    <StyledStackContainer>
      <StyledTablerHeader direction="row">
        <StyledHeader>Heat Map of Parameters</StyledHeader>

        {/* Legend */}
        <Stack direction="row" gap="8px" alignItems="center">
          {heatMapTableStatusBtn?.map(
            (info: HeatMapTableStatusBtn, index: number) => (
              <React.Fragment key={index}>
                <StyledNameBox sx={{ backgroundColor: info.color }} />
                <StyledStatusName>{info.name}</StyledStatusName>
              </React.Fragment>
            )
          )}
        </Stack>
      </StyledTablerHeader>

      <Box sx={{ height: "2px", backgroundColor: "#D7D7D7", mb: "20px" }} />



      {hitMapHeaderData.length > 0 && hitMapHeaderData ? 
        (

          <>
            <StyledTableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <StyledStickyHeaderTableCellLeft0></StyledStickyHeaderTableCellLeft0>

                    {hitMapHeaderData.length > 0 && hitMapHeaderData.map((header)=> (
                      <StyledHeaderTableCell>
                          <Tooltip title={header || ""}>
                            <span>

                              {header.length > 20? (
                                <>
                                  {header.slice(0, 15)}...
                                </>
                              ) : (
                                header
                              )}
                            </span>
                          </Tooltip>
                        
                      </StyledHeaderTableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {hitMapRowData.length > 0 && hitMapRowData.map((row, rowIndex) => (
                  
                    <TableRow key={rowIndex}>
                      <StyledStickyHeaderTableCellLeft0>{row.centreName}</StyledStickyHeaderTableCellLeft0>

                      {hitMapHeaderData.map((header, headerIndex) => (
                        <StyledBodyTableCell 
                          key={headerIndex}
                          sx={{ 
                            bgcolor: row[header] == "Red" ? "#E75A47" : 
                            row[header] == "Orange" ? "#FFC85D" :  
                            row[header] == "Green" ? "#54C95B" : "#eeebec",
                          }}
                        >
                        {row[header] ? "" : "-"}
                        </StyledBodyTableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>

            <Box 
              sx={{
                width:"100%", 
                display:"flex", 
                justifyContent:"center", 
                alignItems:"center", 
                mt:"20px"
              }}
            >
              <Pagination count={hitMapRowData.length} onChange={handleHitMapTablePagination} />
            </Box>
          </>


        ): (

          <Box sx={{display:"flex", alignItems:"center", justifyContent:"center", height:"300px"}} >
            <Typography>No data found</Typography>
          </Box>
          
        )
      }
        
    </StyledStackContainer>
  );
};


const StyledStackContainer = styled(Stack)({
  width: "100%",
  margin: "0 auto",
  border: "1px solid #D7D7D7",
  marginBottom: "2px",
  borderRadius: "16px",
  boxSizing: "border-box",
  overflow: "hidden",
  backgroundColor: "white",
  padding: "24px",
});


const StyledTablerHeader = styled(Stack)({
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 0px 26px 0px",
});

const StyledHeader = styled(Typography)({
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "16.94px",
});


const StyledStatusName = styled(Typography)({
  fontWeight: "500",
  lineHeight: "20px",
  fontSize: "14px",
  color: "#101828",
});

const StyledNameBox = styled(Box)({
  borderRadius: "4px",
  width: "20px",
  height: "20px",
});

const StyledTableContainer = styled(TableContainer)({
  width: "100%",
  borderRadius: "8px",
  overflowX: "auto",
  minHeight:"300px"
});

const StyledTable = styled(Table)({
  borderCollapse: "separate",
  width: "max-content",
  borderSpacing: "3.2px",
  borderBottom: "none",
});

// Sticky first columns of header (Row No. 1)
const StyledStickyHeaderTableCellLeft0 = styled(TableCell)({
  position: "sticky",
  left: "0",
  zIndex: 5,
  backgroundColor: "#ffffff",
  fontWeight: 500,
  width: "300px",
  fontSize:"12px",
  borderBottom: "none",
  lineHeight:"1px",
  padding:"15px"
});

// Sticky first columns of table body
const StyledStickyBodyTableCellLeft0 = styled(TableCell)({
  position: "sticky",
  left: "0px",
  backgroundColor: "#ffffff",
  minWidth: "200px",
  borderBottom: "none",
  paddingLeft:"2px",
});

// Normal Header & Body Cell Styles
const StyledHeaderTableCell = styled(TableCell)({
  fontWeight: 500,
  fontSize: "13px",
  borderBottom: "none",
  width: "130px",
  height: "20px !important",
  textAlign: "center",
  padding: "0px",
  color: "#242424",
});

const StyledBodyTableCell = styled(TableCell)({
  fontSize: "14px",
  // width: "150px",
  height: "20px !important",
  textAlign: "center",
  borderBottom: "none",
  padding: "0px",
  margin: "10px 10px",
});

export default HeatMapTable;
